/**
 * Demo-workspace description copy — built from the server-computed DemoStats
 * (GET /demo-data/status) so every surface (Welcome modal, Data Sources card,
 * delete-confirm dialog) describes the SAME numbers the dataset actually seeds.
 * This is what kills the drift that left hand-maintained "20 people / 8 sprints /
 * 140 tickets" strings stale after the dataset grew.
 *
 * Each builder accepts `DemoStats | null` and returns a graceful, number-free
 * sentence when stats haven't loaded yet (the brief window before /status
 * resolves) — never a blank or a "0".
 */
import type { DemoStats } from '../types';

const n = (x: number) => x.toLocaleString();

// "8 squadrons (4 running sprints, 4 running continuous-flow Kanban)"
function squadronPhrase(s: DemoStats): string {
  return `${s.squadronCount} squadrons (${s.sprintSquadronCount} running sprints, ` +
    `${s.kanbanSquadronCount} running continuous-flow Kanban)`;
}

// "6 closed + 1 active + 2 upcoming sprints"
function sprintPhrase(s: DemoStats): string {
  return `${s.closedSprintCount} closed + ${s.activeSprintCount} active + ` +
    `${s.futureSprintCount} upcoming sprints`;
}

/** Full "Load …" pitch on the Data Sources card. */
export function demoLoadDescription(s: DemoStats | null): string {
  if (!s) {
    return 'Load Dunder Mifflin Platform — a full sample organization with people across ' +
      'departments and squadrons, several sprints, a product backlog, and Jira-, Excel-, ' +
      'and Avium-sourced tickets. It sits alongside any real data you have and can be ' +
      'deleted any time.';
  }
  return `Load Dunder Mifflin Platform — ${n(s.peopleCount)} people across ` +
    `${s.departmentCount} departments and ${squadronPhrase(s)}, ${sprintPhrase(s)}, ` +
    `a groomed product backlog, and ~${n(s.ticketCountApprox)} tickets covering Jira sync, ` +
    `Excel import, in-app edits, and Avium-created sources. It sits alongside any real ` +
    `data you have and can be deleted any time.`;
}

/** Middle clause of the Welcome-modal sentence ("We've prepared a sample project — … — with {this}."). */
export function demoWelcomeDescription(s: DemoStats | null): string {
  if (!s) {
    return 'people across departments and squadrons, several sprints, a product backlog, ' +
      'and tickets across Jira-imported, Excel-imported, and Avium-created sources';
  }
  return `${s.departmentCount} departments, ${n(s.peopleCount)} people across ` +
    `${squadronPhrase(s)}, ${sprintPhrase(s)}, a groomed product backlog, and ` +
    `~${n(s.ticketCountApprox)} tickets across Jira-imported, Excel-imported, and ` +
    `Avium-created sources`;
}

/** Delete-confirm fragment: "the 49 demo resources and 5 demo departments". */
export function demoDeleteSummary(s: DemoStats | null): string {
  if (!s) return 'the demo resources and departments';
  return `the ${n(s.peopleCount)} demo resources and ${s.departmentCount} demo departments`;
}
